import React, { useMemo } from 'react'
import { InputParameterHierarchyType, MeetingAgendaInputParameters } from './model'
import { MeetingTemplateInputParametersHierarchyConfigModal } from './MeetingTemplateInputParametersHierarchyConfigModal'
import { MeetingTemplateGlobalInputParameterConfigForm } from './Form/MeetingTemplateGlobalInputParameterConfigForm'
import { MeetingTemplateCurrentUserInputParameterConfigForm } from './Form/MeetingTemplateCurrentUserInputParameterConfigForm'
import { MeetingTemplateTeamMemberInputParameterConfigForm } from './Form/MeetingTemplateTeamMemberInputParameterConfigForm'
import { useAccount } from '../../common/useAccount'
import { useGetPromptSegmentsQuery } from '../../store/ai/promptSegment'

export interface MeetingAgendaInputParametersHierarchyConfigModalProps {
  show: boolean
  onHide: () => void
  meetingTemplateId: string
  defaultInputParameters: MeetingAgendaInputParameters | null
}

export const MeetingAgendaInputParametersHierarchyConfigModal: React.FC<MeetingAgendaInputParametersHierarchyConfigModalProps> = ({
  show,
  onHide,
  meetingTemplateId,
  defaultInputParameters,
}) => {
  const { accountId } = useAccount()

  const { data: agendaPromptSegments = [] } = useGetPromptSegmentsQuery(
    {
      accountId: accountId!,
      type: 'meeting-agenda',
    },
    {
      skip: !accountId,
    },
  )

  const inputParametersSchema = useMemo(() => {
    return {
      useIconLayout: true,
      properties: {
        fixed: {
          title: 'Fixed topics',
          type: 'array',
          description: 'Add specific items you want to discuss in meetings using this template.',
          icon: 'bi-list-ul',
          items: {
            type: 'string',
          },
        },
        ...(agendaPromptSegments.length > 0 && {
          smartSuggestions: {
            title: 'Smart Suggestions',
            type: 'array',
            description: 'Select topics for the AI Assistant to automatically generate relevant agenda items.',
            icon: 'bi-stars',
            items: {
              type: 'string',
            },
            uniqueItems: true,
            options: agendaPromptSegments,
          },
        }),
        customInstructions: {
          title: 'Custom instructions to AI Assistant',
          type: 'string',
          description: 'Write a custom prompt for the AI Assistant to generate agenda items tailored to this Meeting Type.',
          icon: 'bi-stars',
          maxLength: 600,
        },
      },
    }
  }, [agendaPromptSegments])

  return (
    <MeetingTemplateInputParametersHierarchyConfigModal
      show={show}
      onHide={onHide}
      type={InputParameterHierarchyType.MEETING_AGENDA_CONFIG}
      typeId={'agenda-conf'}
      name={'Meeting Agenda Template'}
      inputParametersSchema={inputParametersSchema}
      defaultInputParameters={defaultInputParameters}
      meetingTemplateId={meetingTemplateId}
      MeetingTemplateConfig={MeetingTemplateGlobalInputParameterConfigForm}
      CurrentUserConfig={MeetingTemplateCurrentUserInputParameterConfigForm}
      TeamMembersConfig={MeetingTemplateTeamMemberInputParameterConfigForm}
    />
  )
}
